// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import {
  DnaSyngoviaNodeEType,
  DnaSyngoviaNodeType,
} from '../../interfaces/dna-syngovia-node';
import { DnaSyngoviaCardSectionDetailsModel } from '../../model/dna-syngovia-card-section-details-model';
import { DnaSyngoviaGroupNodeSelectionModel } from '../../model/dna-syngovia-group-node-selection-model';
import { DnaSyngoviaSelectionType } from '../../model/dna-syngovia-selection-model';
import { GroupNodeSelectionModelByType } from '../../services/dna-syngovia-group-node.service';
import { DnaSyngoviaIconService } from '../../services/dna-syngovia-icon.service';

export type SelectedNodeChangeEvent = {
  currentValue: DnaSyngoviaSelectionType;
  previousValue: DnaSyngoviaSelectionType | undefined;
};

export type ChangeCardEventSourceType =
  | 'initial'
  | 'change-btn'
  | 'menu-item'
  | 'table-selection';

export type CardSectionDetailsType = {
  filler?: number;
  rows: DnaSyngoviaCardSectionDetailsModel[];
};

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule, MatTooltipModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-comparable-card-node',
  templateUrl: './dna-syngovia-comparable-card-node.component.html',
  styleUrls: ['./dna-syngovia-comparable-card-node.component.scss'],
})
export class DnaSyngoviaComparableCardNodeComponent implements OnInit {
  @Input({ required: true })
  groupNodeSelectionModelByType!: GroupNodeSelectionModelByType;
  @Output() selectedNodeChange = new EventEmitter<SelectedNodeChangeEvent>();
  @Output() removeNodeChange =
    new EventEmitter<DnaSyngoviaComparableCardNodeComponent>();

  @Input() currentGroupNodeSelectionModel:
    | DnaSyngoviaGroupNodeSelectionModel
    | undefined;
  @Input() currentNodeSelection: DnaSyngoviaSelectionType | undefined;
  private previousNodeSelection: DnaSyngoviaSelectionType | undefined;
  protected readonly NOT_FOUND = '-';

  protected groupNodeSelectionModelByTypeKeys: string[] = [];
  protected readonly changeCardEvent$ = new BehaviorSubject<{
    source: ChangeCardEventSourceType;
  }>({ source: 'initial' });

  protected readonly sectionColumnsDictionary: {
    [key: string]: {
      identification: CardSectionDetailsType;
      network: CardSectionDetailsType;
      device?: CardSectionDetailsType;
    };
  } = {
    server: {
      identification: {
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'serialNumber' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'swVersion' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'handover' }),
        ],
      },
      network: {
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'aet' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'ip' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'dns' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'subnet' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'gateway' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'iloIp' }),
        ],
      },
      device: {
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'hpSn' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'systemModel' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'sku' }),
        ],
      },
    },
    client: {
      identification: {
        filler: 1,
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'swVersion' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'os' }),
        ],
      },
      network: {
        filler: 4,
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'hostname' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'ip' }),
        ],
      },
    },
    default: {
      identification: {
        filler: 1,
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'logicalName' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'modelName' }),
        ],
      },
      network: {
        filler: 4,
        rows: [
          new DnaSyngoviaCardSectionDetailsModel({ property: 'hostname' }),
          new DnaSyngoviaCardSectionDetailsModel({ property: 'ip' }),
        ],
      },
    },
  };

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dnaSyngoviaIconService: DnaSyngoviaIconService
  ) {}

  public ngOnInit(): void {
    if (this.groupNodeSelectionModelByType) {
      this.groupNodeSelectionModelByTypeKeys = Object.keys(
        this.groupNodeSelectionModelByType
      );
    }

    if (this.currentGroupNodeSelectionModel && this.currentNodeSelection) {
      this.dispatchChangeCardEvent('table-selection');
    }
  }

  protected onModalityMenuItemClick(
    selectionModel: DnaSyngoviaGroupNodeSelectionModel
  ): void {
    this.clearModalitySelection();
    this.currentGroupNodeSelectionModel = selectionModel;
  }

  private clearModalitySelection(): void {
    this.currentGroupNodeSelectionModel = undefined;
    this.deselectCurrentNodeSelection();
    this.changeDetectorRef.detectChanges();
  }

  protected onNodeClicked(nodeSelection: DnaSyngoviaSelectionType): void {
    this.selectedNodeChange.emit({
      currentValue: nodeSelection,
      previousValue: this.previousNodeSelection,
    });

    this.deselectCurrentNodeSelection();
    this.previousNodeSelection = this.currentNodeSelection = nodeSelection;
    nodeSelection.selected = true;

    this.dispatchChangeCardEvent('menu-item');
  }

  protected nodeDescription(node: DnaSyngoviaNodeType): string {
    if (node?.type?.includes(DnaSyngoviaNodeEType.SERVER)) {
      return `${node.hostname ?? this.NOT_FOUND} (SN ${
        node.serialNumber ?? this.NOT_FOUND
      })`;
    }

    if (node?.type?.includes(DnaSyngoviaNodeEType.CLIENT)) {
      return `${node.hostname ?? this.NOT_FOUND}`;
    }

    return `${node?.logicalName ?? this.NOT_FOUND}`;
  }

  private applyAccordionStyle() {
    const sheetHosts = new CSSStyleSheet();
    const hosts = document.querySelectorAll('.accordion');

    sheetHosts.replaceSync(`
      .header-wrapper { 
        background-color: rgba(0, 0, 0, 0.05);
      }
    `);

    hosts.forEach((host) => {
      host?.shadowRoot?.adoptedStyleSheets.push(sheetHosts);
    });
  }

  protected onButtonChangeClick(): void {
    this.dispatchChangeCardEvent('change-btn');
  }

  private dispatchChangeCardEvent(source: ChangeCardEventSourceType): void {
    this.changeCardEvent$.next({ source });

    if (source === 'menu-item' || source === 'table-selection') {
      setTimeout(() => {
        this.applyAccordionStyle();
      }, 50);
    }
  }

  protected onRemoveNodeClick(): void {
    this.deselectCurrentNodeSelection();
    this.removeNodeChange.emit(this);
  }

  private deselectCurrentNodeSelection(): void {
    if (this.currentNodeSelection) {
      this.currentNodeSelection.selected = false;
      this.currentNodeSelection = undefined;
    }
  }

  protected displayPropertyValue(property: string): string {
    let result = this.NOT_FOUND;
    if (this.currentNodeSelection) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      result =
        (this.currentNodeSelection.value as any)[property] ?? this.NOT_FOUND;

      if (
        [DnaSyngoviaNodeEType.CLIENT, DnaSyngoviaNodeEType.SERVER].includes(
          this.currentNodeSelection.value.type
        ) &&
        ['ip', 'dns', 'subnet', 'gateway', 'iloIp'].includes(property)
      ) {
        result = this.concatProperyValues(
          this.currentNodeSelection.value.networkAdapters as {
            [key: string]: string;
          }[],
          property
        );
      }
    }

    return result;
  }

  private concatProperyValues(
    list: { [key: string]: string }[],
    property: string,
    separator = '; '
  ): string {
    let result: string | undefined = list
      ?.filter((item) => item[property] !== null)
      .map((item) => item[property])
      .join(separator);

    if (result === '') {
      result = undefined;
    }

    return result ?? this.NOT_FOUND;
  }

  protected hasOverflow = (element: HTMLElement): boolean => {
    return element.scrollWidth > element.clientWidth;
  };
}
